/**
 * Asynchronous mean-luminance measurement of the final frame (for the flash limiter). Downsamples
 * to 16×16 on the GPU and reads back through a pixel-pack buffer with a fence, so the frame loop
 * never stalls; results arrive one or two frames late.
 */
import { createRenderTarget, uniform, type GL, type Program, type RenderTarget } from './gl/gl';
import { UNIT_INPUT } from './frame';

const SIZE = 16;

export class LumaProbe {
  private readonly target: RenderTarget;
  private readonly pbo: WebGLBuffer;
  private fence: WebGLSync | null = null;
  private readonly pixels = new Uint8Array(SIZE * SIZE * 4);
  value = 0;

  constructor(
    private readonly gl: GL,
    private readonly blit: Program,
  ) {
    this.target = createRenderTarget(gl, SIZE, SIZE);
    const pbo = gl.createBuffer();
    if (!pbo) throw new Error('createBuffer failed');
    this.pbo = pbo;
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, pbo);
    gl.bufferData(gl.PIXEL_PACK_BUFFER, this.pixels.byteLength, gl.STREAM_READ);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
  }

  /** Collect a finished measurement (if any) and start a new one from `source`. */
  sample(source: WebGLTexture): number {
    const gl = this.gl;
    if (this.fence) {
      const status = gl.clientWaitSync(this.fence, 0, 0);
      if (status === gl.TIMEOUT_EXPIRED) return this.value; // still in flight
      gl.deleteSync(this.fence);
      this.fence = null;
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, this.pbo);
      gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, this.pixels);
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
      let sum = 0;
      for (let i = 0; i < this.pixels.length; i += 4) {
        sum +=
          0.2126 * this.pixels[i]! + 0.7152 * this.pixels[i + 1]! + 0.0722 * this.pixels[i + 2]!;
      }
      this.value = sum / (SIZE * SIZE * 255);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.target.fbo);
    gl.viewport(0, 0, SIZE, SIZE);
    gl.useProgram(this.blit.program);
    gl.activeTexture(gl.TEXTURE0 + UNIT_INPUT);
    gl.bindTexture(gl.TEXTURE_2D, source);
    gl.uniform1i(uniform(gl, this.blit, 'uTex'), UNIT_INPUT);
    gl.uniform1f(uniform(gl, this.blit, 'uGain'), 1);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, this.pbo);
    gl.readPixels(0, 0, SIZE, SIZE, gl.RGBA, gl.UNSIGNED_BYTE, 0);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
    this.fence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
    return this.value;
  }
}
